import { Box, Button, Typography } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React from "react";
import { richText } from "./richText";
import { useIsCoarsePointerDevice } from "./deviceInput";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CategoriesIntroductionPage {
  title?: string;
  instructions?: string[];
}

export interface CategoriesConfig {
  introduction?: CategoriesIntroductionPage[];
  key_assignment: {
    randomize: boolean;
    left_hand_keys: string[];
    right_hand_keys: string[];
    "1d_categories": string[];
    "2d_categories": string[];
  };
  phases: Array<{
    phase: number;
    structure: "1D" | "2D" | "mixed";
    rounds: number;
    trials_per_round: number;
    response_time_limit_ms: number;
    isi_ms: number;
    feedback: boolean;
    tip_available_time_ms?: number;
    categories: string[];
    introduction?: {
      title?: string;
      text?: string;
      instructions?: string[];
    };
  }>;
}

export interface CategoriesTrialResult {
  trial_number: number;
  category: string;
  rect_height_px: number;
  door_position_px: number;
  user_response: string | null;
  is_correct: boolean;
  response_time_ms: number;
  timed_out: boolean;
}

export interface CategoriesRoundResult {
  round_number: number;
  correct_count: number;
  error_count: number;
  omission_count: number;
  trials: CategoriesTrialResult[];
}

export interface CategoriesPhaseResult {
  phase: number;
  rounds: CategoriesRoundResult[];
}

export interface CategoriesTaskAnswer {
  key_assignment: Record<string, string>;
  phases: CategoriesPhaseResult[];
}

interface Props {
  onAnswerChange: (taskAnswer?: CategoriesTaskAnswer) => void;
  categoriesConfig?: Partial<CategoriesConfig>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const HEIGHT_MIN = 8;
const HEIGHT_MAX = 538;
const HEIGHT_THRESHOLD = 269;
const HEIGHT_SD = 80;
const DOOR_MIN_PX = 8;
const DOOR_MAX_PX = 478;
const DOOR_THRESHOLD_PX = 239;
const DOOR_CANVAS_WIDTH = 478; // reference width for door_position_px output
const FEEDBACK_MS = 500;
// Contagem exibida antes de iniciar cada uma das três fases.
const START_COUNTDOWN_SECONDS = 3;

const CYAN = "#00E5FF";
const MAGENTA = "#FF00E5";
// Borda dos botões de resposta na fase mista, onde a cor por categoria sai de cena.
const NEUTRAL_BUTTON_BORDER = "#E8E8F0";

const DEFAULT_CATEGORIES_INTRO_TITLE = "Tarefa de Aprendizagem por Categorias";
// Cópia conforme as imagens de especificação. Os trechos em <strong> passam por
// richText() no render — ver richText.tsx.
export const DEFAULT_CATEGORIES_INTRODUCTION_PAGES: Array<Required<CategoriesIntroductionPage>> = [
  {
    title: DEFAULT_CATEGORIES_INTRO_TITLE,
    instructions: [
      "<strong>Instrução geral:</strong> Nesta tarefa, diferentes figuras aparecerão, <strong>uma de cada vez</strong>, no centro da tela. Cada figura é composta por um <strong>retângulo com uma linha vertical em seu interior.</strong> Observe atentamente cada figura. De uma figura para outra, <strong>a altura do retângulo e a posição da linha podem variar</strong>. Ao longo da tarefa, você deverá observar essas características para aprender a classificar corretamente as figuras.",
    ],
  },
];

const DEFAULT_CONFIG: CategoriesConfig = {
  key_assignment: {
    randomize: true,
    left_hand_keys: ["A", "S"],
    right_hand_keys: ["K", "L"],
    "1d_categories": ["category_a", "category_b"],
    "2d_categories": ["category_c", "category_d"],
  },
  phases: [
    { phase: 1, structure: "1D", rounds: 4, trials_per_round: 50, response_time_limit_ms: 5000, isi_ms: 1000, feedback: true, tip_available_time_ms: 10000, categories: ["A", "B"] },
    { phase: 2, structure: "2D", rounds: 11, trials_per_round: 50, response_time_limit_ms: 5000, isi_ms: 1000, feedback: true, tip_available_time_ms: 10000, categories: ["C", "D"] },
    { phase: 3, structure: "mixed", rounds: 4, trials_per_round: 100, response_time_limit_ms: 3000, isi_ms: 1000, feedback: false, tip_available_time_ms: 10000, categories: ["A", "B", "C", "D"] },
  ],
};

type Screen = "intro" | "phase_intro" | "isi" | "trial" | "feedback" | "round_break" | "done";
type FeedbackType = "correct" | "error" | "omission";

interface Stimulus {
  category: string;
  categoryKey: string;
  rect_height_px: number;
  door_position_px: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const shuffleArray = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const randInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Truncated normal distribution via Box-Muller (retries until value falls in range)
const normalRandTruncated = (mean: number, sd: number, min: number, max: number): number => {
  for (let i = 0; i < 200; i++) {
    const u1 = Math.max(Math.random(), 1e-10);
    const u2 = Math.random();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const v = Math.round(mean + sd * z);
    if (v >= min && v <= max) return v;
  }
  return Math.round((min + max) / 2); // fallback
};

const categoryToKey = (cat: string): string => `category_${cat.toLowerCase()}`;

const buildKeyAssignment = (cfg: CategoriesConfig["key_assignment"]): Record<string, string> => {
  const leftKeys = cfg.randomize ? shuffleArray([...cfg.left_hand_keys]) : [...cfg.left_hand_keys];
  const rightKeys = cfg.randomize ? shuffleArray([...cfg.right_hand_keys]) : [...cfg.right_hand_keys];
  const cats1D = cfg.randomize ? shuffleArray([...cfg["1d_categories"]]) : [...cfg["1d_categories"]];
  const cats2D = cfg.randomize ? shuffleArray([...cfg["2d_categories"]]) : [...cfg["2d_categories"]];
  const result: Record<string, string> = {};
  // Each sub-structure: one category gets left-hand key, other gets right-hand key
  if (cats1D[0]) result[cats1D[0]] = leftKeys[0] ?? "A";
  if (cats1D[1]) result[cats1D[1]] = rightKeys[0] ?? "K";
  if (cats2D[0]) result[cats2D[0]] = leftKeys[1] ?? "S";
  if (cats2D[1]) result[cats2D[1]] = rightKeys[1] ?? "L";
  return result;
};

const genStimulus = (category: string): Stimulus => {
  let height: number;
  let doorPx: number;
  // Height means: A/D low (~100), B/C high (~430). Door for C/D constrained by threshold.
  switch (category) {
    case "A": // low height, door anywhere
      height = normalRandTruncated(100, HEIGHT_SD, HEIGHT_MIN, HEIGHT_THRESHOLD - 1);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
      break;
    case "B": // high height, door anywhere
      height = normalRandTruncated(430, HEIGHT_SD, HEIGHT_THRESHOLD, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
      break;
    case "C": // high height, door left
      height = normalRandTruncated(430, HEIGHT_SD, HEIGHT_THRESHOLD, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_THRESHOLD_PX - 1);
      break;
    case "D": // low height, door right
      height = normalRandTruncated(100, HEIGHT_SD, HEIGHT_MIN, HEIGHT_THRESHOLD - 1);
      doorPx = randInt(DOOR_THRESHOLD_PX, DOOR_MAX_PX);
      break;
    default:
      height = normalRandTruncated(HEIGHT_THRESHOLD, HEIGHT_SD, HEIGHT_MIN, HEIGHT_MAX);
      doorPx = randInt(DOOR_MIN_PX, DOOR_MAX_PX);
  }
  return {
    category,
    categoryKey: categoryToKey(category),
    rect_height_px: height,
    door_position_px: doorPx,
  };
};

const buildTrialSequence = (categories: string[], count: number): string[] => {
  const trialsPerCat = Math.floor(count / categories.length);
  const seq: string[] = [];
  for (const cat of categories) {
    for (let i = 0; i < trialsPerCat; i++) seq.push(cat);
  }
  const remainder = count - seq.length;
  for (let i = 0; i < remainder; i++) seq.push(categories[i % categories.length]);
  return shuffleArray(seq);
};

const resolveConfig = (raw?: Partial<CategoriesConfig>): CategoriesConfig => {
  if (!raw) return DEFAULT_CONFIG;
  return {
    key_assignment: raw.key_assignment ?? DEFAULT_CONFIG.key_assignment,
    phases: (raw.phases && raw.phases.length > 0)
      ? (raw.phases as CategoriesConfig["phases"])
      : DEFAULT_CONFIG.phases,
  };
};

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

// A cópia das telas de fase vem das imagens de especificação, mas as teclas
// continuam sendo resolvidas em runtime (key_assignment é randomizado por
// participante). O tempo de resposta e a quantidade de rodadas também saem da
// config da fase, para o texto não contradizer o comportamento real.
const getPhaseIntroDefaults = (
  keyAssignment: Record<string, string>,
  phase?: CategoriesConfig["phases"][number]
): Record<string, { title: string; text: string; instructions?: string[] }> => {
  const key = (cat: string) => keyAssignment[categoryToKey(cat)] ?? "?";
  const responseWindow = pluralize(
    Math.max(1, Math.round((phase?.response_time_limit_ms ?? 5000) / 1000)),
    "segundo",
    "segundos"
  );
  const roundCount = pluralize(Math.max(1, phase?.rounds ?? 1), "rodada", "rodadas");
  const tipReminder =
    "um lembrete com as opções de resposta será exibido na parte inferior da tela. Após alguns instantes, esse lembrete desaparecerá automaticamente.";

  return {
    "1D": {
      title: "Fase 1",
      text:
        "<p>A tarefa possui três fases. Nesta primeira fase, você deverá classificar as figuras considerando apenas a <strong>ALTURA</strong> do retângulo, se é <strong>ALTO ou BAIXO</strong>.</p>" +
        "<p>Para responder:</p>" +
        "<ul>" +
        `<li>Pressione rapidamente a <strong>tecla ${key("A")}</strong> se o retângulo for <strong>BAIXO</strong>;</li>` +
        `<li>Pressione rapidamente a <strong>tecla ${key("B")}</strong> se o retângulo for <strong>ALTO</strong>.</li>` +
        `<li>Você terá <strong>${responseWindow}</strong> para dar cada resposta.</li>` +
        "</ul>" +
        "<p>Lembre-se de classificar cada retângulo somente em relação à sua <strong>ALTURA</strong>. A posição da linha não importa neste momento.</p>" +
        `<p>No início da tarefa, ${tipReminder}</p>`,
    },
    "2D": {
      title: "Fase 2",
      text:
        "<p>Agora, a tarefa continuará com uma nova etapa. A partir deste momento, classifique cada retângulo de acordo com a sua <strong>ALTURA</strong> e de acordo com a <strong>POSIÇÃO DA LINHA</strong> em seu interior.</p>" +
        "<p>Para responder:</p>" +
        "<ul>" +
        `<li>Pressione rapidamente a <strong>tecla ${key("C")}</strong> se o retângulo for <strong>ALTO com a LINHA À ESQUERDA</strong>;</li>` +
        `<li>Pressione rapidamente a <strong>tecla ${key("D")}</strong> se o retângulo for <strong>BAIXO com a LINHA À DIREITA</strong>.</li>` +
        `<li>Você terá <strong>${responseWindow}</strong> para dar cada resposta.</li>` +
        "</ul>" +
        `<p>Novamente, no início desta fase, ${tipReminder}</p>`,
    },
    "mixed": {
      title: "Fase 3",
      text: `<p>Agora, você iniciará a última fase da tarefa. Ela apresenta ${roundCount}. Continue observando <strong>atentamente</strong> cada figura e responda da melhor forma possível. Utilize tudo o que você aprendeu nas fases anteriores para <strong>classificar corretamente</strong> as figuras.</p>`,
    },
  };
};

const CAT_DESCS: Record<string, string> = {
  A: "Baixo",
  B: "Alto",
  C: "Alto + linha à esquerda",
  D: "Baixo + linha à direita",
};

// ─── Component ────────────────────────────────────────────────────────────────

const CategoriesQuestion: React.FC<Props> = ({ onAnswerChange, categoriesConfig }) => {
  const cfg = React.useMemo(() => resolveConfig(categoriesConfig), [categoriesConfig]);
  const isCoarsePointer = useIsCoarsePointerDevice();

  const introductionPages = React.useMemo<Array<Required<CategoriesIntroductionPage>>>(() => {
    const raw = categoriesConfig?.introduction;
    // Aceita o formato atual (array de páginas) e o legado (objeto único { title, text }).
    const pages = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object"
        ? [
          {
            title: (raw as { title?: string }).title,
            instructions: (raw as { text?: string }).text
              ? [(raw as { text?: string }).text as string]
              : [],
          },
        ]
        : null;

    if (!pages || pages.length === 0) return DEFAULT_CATEGORIES_INTRODUCTION_PAGES;

    return pages.map((page) => ({
      title: page?.title ?? DEFAULT_CATEGORIES_INTRO_TITLE,
      instructions: Array.isArray(page?.instructions) ? page.instructions : [],
    }));
  }, [categoriesConfig]);

  // Key assignment and color assignment are randomized once on mount
  const [keyAssignment] = React.useState<Record<string, string>>(
    () => buildKeyAssignment(cfg.key_assignment)
  );
  // A/B get one color, C/D get the other — randomized per participant
  const [colorAssignment] = React.useState<{ ab: string; cd: string }>(() =>
    Math.random() < 0.5 ? { ab: CYAN, cd: MAGENTA } : { ab: MAGENTA, cd: CYAN }
  );

  const keyToCategoryKey = React.useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const [catKey, k] of Object.entries(keyAssignment)) {
      map[k.toUpperCase()] = catKey;
    }
    return map;
  }, [keyAssignment]);

  const validKeys = React.useMemo(
    () => new Set(Object.values(keyAssignment).map(k => k.toUpperCase())),
    [keyAssignment]
  );

  // Timers
  const timersRef = React.useRef<number[]>([]);
  const addTimer = (id: number) => timersRef.current.push(id);
  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // Task progress (refs — no re-render needed)
  const phaseIdxRef = React.useRef(0);
  const roundIdxRef = React.useRef(0);
  const trialIdxRef = React.useRef(0);
  const trialSeqRef = React.useRef<string[]>([]);
  const phaseResultsRef = React.useRef<CategoriesPhaseResult[]>([]);
  const currentRoundTrialsRef = React.useRef<CategoriesTrialResult[]>([]);
  const roundCountsRef = React.useRef({ correct: 0, error: 0, omission: 0 });
  const trialStartRef = React.useRef(0);
  const handledRef = React.useRef(false);
  const stimulusRef = React.useRef<Stimulus | null>(null);
  const tipHiddenPhaseRef = React.useRef<number>(-1);
  const tipTimerRef = React.useRef<number | null>(null);
  // Evita disparo duplo da contagem (botão + barra de espaço).
  const isCountingDownRef = React.useRef(false);

  // UI state
  const [screen, setScreen] = React.useState<Screen>("intro");
  const [introPageIndex, setIntroPageIndex] = React.useState(0);
  const [stimulus, setStimulus] = React.useState<Stimulus | null>(null);
  const [feedbackType, setFeedbackType] = React.useState<FeedbackType | null>(null);
  const [timeLeftPct, setTimeLeftPct] = React.useState(1);
  const [phaseIdx, setPhaseIdx] = React.useState(0);
  const [roundIdx, setRoundIdx] = React.useState(0);
  // 0 enquanto o bloco ainda não apresentou a primeira figura (ISI de entrada).
  const [currentTrialNum, setCurrentTrialNum] = React.useState(0);
  const [roundStats, setRoundStats] = React.useState({ correct: 0, error: 0, omission: 0 });
  const [tipVisible, setTipVisible] = React.useState(true);
  const [isStartingCountdown, setIsStartingCountdown] = React.useState(false);
  const [countdownValue, setCountdownValue] = React.useState(0);
  const [phaseSummaries, setPhaseSummaries] = React.useState<
    Array<{ phase: number; correct: number; error: number; omission: number }>
  >([]);

  // Dark box top position — measured before paint so height fills to viewport bottom
  const darkBoxRef = React.useRef<HTMLDivElement>(null);
  const [darkBoxTop, setDarkBoxTop] = React.useState(0);
  React.useLayoutEffect(() => {
    const measure = () => {
      if (darkBoxRef.current) setDarkBoxTop(darkBoxRef.current.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [screen]);

  // Stimulus area size — drives bus scale via ResizeObserver
  const stimAreaRef = React.useRef<HTMLDivElement>(null);
  const [stimAreaH, setStimAreaH] = React.useState(HEIGHT_MAX);
  React.useEffect(() => {
    const el = stimAreaRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      setStimAreaH(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [screen]);

  const busScale = Math.min(1, Math.max(0.2, (stimAreaH - 16) / HEIGHT_MAX));

  // Forward ref — avoids stale closures in recursive timer chains
  const startTrialRef = React.useRef<() => void>(() => { });

  const finishTask = React.useCallback(() => {
    clearTimers();
    const answer: CategoriesTaskAnswer = {
      key_assignment: { ...keyAssignment },
      phases: phaseResultsRef.current.map(p => ({
        phase: p.phase,
        rounds: p.rounds.map(r => ({
          round_number: r.round_number,
          correct_count: r.correct_count,
          error_count: r.error_count,
          omission_count: r.omission_count,
          trials: [...r.trials],
        })),
      })),
    };
    setPhaseSummaries(
      answer.phases.map((p) => ({
        phase: p.phase,
        correct: p.rounds.reduce((sum, r) => sum + r.correct_count, 0),
        error: p.rounds.reduce((sum, r) => sum + r.error_count, 0),
        omission: p.rounds.reduce((sum, r) => sum + r.omission_count, 0),
      }))
    );
    setScreen("done");
    onAnswerChange(answer);
  }, [clearTimers, keyAssignment, onAnswerChange]);

  const saveRoundAndBreak = React.useCallback(() => {
    const phaseNum = cfg.phases[phaseIdxRef.current].phase;
    const roundResult: CategoriesRoundResult = {
      round_number: roundIdxRef.current + 1,
      correct_count: roundCountsRef.current.correct,
      error_count: roundCountsRef.current.error,
      omission_count: roundCountsRef.current.omission,
      trials: [...currentRoundTrialsRef.current],
    };
    let phaseResult = phaseResultsRef.current.find(p => p.phase === phaseNum);
    if (!phaseResult) {
      phaseResult = { phase: phaseNum, rounds: [] };
      phaseResultsRef.current = [...phaseResultsRef.current, phaseResult];
    }
    phaseResult.rounds.push(roundResult);
    setRoundStats({ ...roundCountsRef.current });
    setScreen("round_break");
  }, [cfg.phases]);

  const startTrial = React.useCallback(() => {
    clearTimers();
    if (trialIdxRef.current >= trialSeqRef.current.length) {
      saveRoundAndBreak();
      return;
    }
    const phase = cfg.phases[phaseIdxRef.current];
    const category = trialSeqRef.current[trialIdxRef.current];
    const stim = genStimulus(category);
    stimulusRef.current = stim;
    handledRef.current = false;
    setCurrentTrialNum(trialIdxRef.current + 1);
    setStimulus(stim);
    setFeedbackType(null);
    setTimeLeftPct(1);
    trialStartRef.current = performance.now();
    setScreen("trial");

    // A dica vale por tip_available_time_ms a partir do primeiro trial da fase,
    // então só o primeiro trial agenda (tipTimerRef == null). Agendar a cada
    // trial deixava timers órfãos: em fases curtas, a fase acabava antes do
    // prazo e o resto disparava já dentro da fase seguinte, escondendo a dica
    // dela. O índice é capturado no agendamento — o callback ignora o disparo
    // se a fase já trocou, em vez de ler phaseIdxRef no momento em que cai.
    if (
      phase.tip_available_time_ms != null &&
      tipHiddenPhaseRef.current !== phaseIdxRef.current &&
      tipTimerRef.current == null
    ) {
      const scheduledForPhase = phaseIdxRef.current;
      setTipVisible(true);
      tipTimerRef.current = window.setTimeout(() => {
        tipTimerRef.current = null;
        if (phaseIdxRef.current !== scheduledForPhase) return;
        tipHiddenPhaseRef.current = scheduledForPhase;
        setTipVisible(false);
      }, phase.tip_available_time_ms);
    }

    const barStart = performance.now();
    addTimer(window.setInterval(() => {
      const pct = Math.max(0, 1 - (performance.now() - barStart) / phase.response_time_limit_ms);
      setTimeLeftPct(pct);
    }, 50) as unknown as number);

    addTimer(window.setTimeout(() => {
      if (handledRef.current) return;
      handledRef.current = true;
      clearTimers();
      const s = stimulusRef.current!;
      currentRoundTrialsRef.current.push({
        trial_number: trialIdxRef.current + 1,
        category: s.category,
        rect_height_px: s.rect_height_px,
        door_position_px: s.door_position_px,
        user_response: null,
        is_correct: false,
        response_time_ms: phase.response_time_limit_ms,
        timed_out: true,
      });
      roundCountsRef.current.omission += 1;
      trialIdxRef.current += 1;
      if (phase.feedback) {
        setFeedbackType("omission");
        setScreen("feedback");
        addTimer(window.setTimeout(() => {
          setScreen("isi");
          addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
        }, FEEDBACK_MS));
      } else {
        setScreen("isi");
        addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
      }
    }, phase.response_time_limit_ms));
  }, [cfg.phases, clearTimers, saveRoundAndBreak]);

  startTrialRef.current = startTrial;

  const handleResponse = React.useCallback((key: string) => {
    if (handledRef.current || screen !== "trial") return;
    handledRef.current = true;
    clearTimers();
    const phase = cfg.phases[phaseIdxRef.current];
    const s = stimulusRef.current!;
    const responseMs = Math.round(performance.now() - trialStartRef.current);
    const responseCatKey = keyToCategoryKey[key.toUpperCase()] ?? null;
    const isCorrect = responseCatKey === s.categoryKey;
    currentRoundTrialsRef.current.push({
      trial_number: trialIdxRef.current + 1,
      category: s.category,
      rect_height_px: s.rect_height_px,
      door_position_px: s.door_position_px,
      user_response: responseCatKey,
      is_correct: isCorrect,
      response_time_ms: responseMs,
      timed_out: false,
    });
    if (isCorrect) roundCountsRef.current.correct += 1;
    else roundCountsRef.current.error += 1;
    trialIdxRef.current += 1;
    const fb: FeedbackType = isCorrect ? "correct" : "error";
    if (phase.feedback) {
      setFeedbackType(fb);
      setScreen("feedback");
      addTimer(window.setTimeout(() => {
        setScreen("isi");
        addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
      }, FEEDBACK_MS));
    } else {
      setScreen("isi");
      addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
    }
  }, [cfg.phases, clearTimers, keyToCategoryKey, screen]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const key = e.key.toUpperCase();
      if (validKeys.has(key) && screen === "trial") {
        e.preventDefault();
        handleResponse(key);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleResponse, screen, validKeys]);

  // O timer da dica não entra em timersRef (sobrevive de propósito às trocas de
  // trial/round), então precisa ser cancelado à parte no unmount.
  React.useEffect(() => () => {
    clearTimers();
    if (tipTimerRef.current != null) {
      clearTimeout(tipTimerRef.current);
      tipTimerRef.current = null;
    }
  }, [clearTimers]);

  const startRound = React.useCallback((pIdx: number, rIdx: number) => {
    const phase = cfg.phases[pIdx];
    phaseIdxRef.current = pIdx;
    roundIdxRef.current = rIdx;
    trialIdxRef.current = 0;
    currentRoundTrialsRef.current = [];
    roundCountsRef.current = { correct: 0, error: 0, omission: 0 };
    // Zera antes do ISI de entrada: sem isso o progresso do bloco novo apareceria
    // cheio, com o valor herdado do bloco anterior, até a primeira figura.
    setCurrentTrialNum(0);
    trialSeqRef.current = buildTrialSequence(phase.categories, phase.trials_per_round);
    setPhaseIdx(pIdx);
    setRoundIdx(rIdx);
    setScreen("isi");
    addTimer(window.setTimeout(() => startTrialRef.current(), phase.isi_ms));
  }, [cfg.phases]);

  const startPhase = React.useCallback((pIdx: number) => {
    if (tipTimerRef.current != null) {
      clearTimeout(tipTimerRef.current);
      tipTimerRef.current = null;
    }
    phaseIdxRef.current = pIdx;
    tipHiddenPhaseRef.current = -1;
    isCountingDownRef.current = false;
    setTipVisible(true);
    setIsStartingCountdown(false);
    setCountdownValue(0);
    setPhaseIdx(pIdx);
    setScreen("phase_intro");
  }, []);

  const advanceIntro = React.useCallback(() => {
    if (introPageIndex < introductionPages.length - 1) {
      setIntroPageIndex((prev) => prev + 1);
      return;
    }
    startPhase(0);
  }, [introPageIndex, introductionPages.length, startPhase]);

  // Exibe "Começando em N..." e só então roda a ação — usado no início das três fases.
  const runWithStartCountdown = React.useCallback(
    (action: () => void) => {
      if (isCountingDownRef.current) return;
      clearTimers();
      isCountingDownRef.current = true;
      setIsStartingCountdown(true);
      setCountdownValue(START_COUNTDOWN_SECONDS);

      const tick = (remaining: number) => {
        if (remaining <= 0) {
          isCountingDownRef.current = false;
          setIsStartingCountdown(false);
          setCountdownValue(0);
          action();
          return;
        }
        setCountdownValue(remaining);
        addTimer(window.setTimeout(() => tick(remaining - 1), 1000));
      };

      addTimer(window.setTimeout(() => tick(START_COUNTDOWN_SECONDS - 1), 1000));
    },
    [clearTimers],
  );

  const beginPhaseRound = React.useCallback(() => {
    runWithStartCountdown(() => startRound(phaseIdxRef.current, 0));
  }, [runWithStartCountdown, startRound]);

  // Barra de espaço avança a introdução e inicia a fase (tela de intro da fase).
  React.useEffect(() => {
    if (screen !== "intro" && screen !== "phase_intro") return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isSpace = e.code === "Space" || e.key === " ";
      if (!isSpace || e.repeat) return;
      e.preventDefault();
      if (screen === "intro") {
        advanceIntro();
      } else {
        beginPhaseRound();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [screen, advanceIntro, beginPhaseRound]);

  const onContinueRound = React.useCallback(() => {
    const pIdx = phaseIdxRef.current;
    const nextRound = roundIdxRef.current + 1;
    const phase = cfg.phases[pIdx];
    if (nextRound >= phase.rounds) {
      const nextPhase = pIdx + 1;
      if (nextPhase >= cfg.phases.length) finishTask();
      else startPhase(nextPhase);
    } else {
      startRound(pIdx, nextRound);
    }
  }, [cfg.phases, finishTask, startPhase, startRound]);

  const getPhaseLegend = (pIdx: number): Array<{ cat: string; key: string; desc: string; color: string }> => {
    const phase = cfg.phases[pIdx];
    if (!phase) return [];
    return phase.categories.map(cat => ({
      cat,
      key: keyAssignment[categoryToKey(cat)] ?? "?",
      desc: CAT_DESCS[cat] ?? cat,
      color: (cat === "A" || cat === "B") ? colorAssignment.ab : colorAssignment.cd,
    }));
  };

  const renderLegend = (items: Array<{ cat: string; key: string; desc: string; color: string }>) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 1 }}>
      {items.map(({ cat, key, desc, color }) => (
        <Box
          key={cat}
          sx={{
            background: "#1E1E2E",
            border: `1px solid ${color}44`,
            borderRadius: 2,
            p: "8px 14px",
            textAlign: "center",
            minWidth: 130,
          }}
        >
          <Typography variant="body1" sx={{ fontFamily: "monospace", fontWeight: 700, color }}>
            [{key}] 
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa", mt: 0.25, textTransform: "uppercase" }}>
            {desc}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderBus = (stim: Stimulus) => {
    const busColor = (stim.category === "A" || stim.category === "B")
      ? colorAssignment.ab
      : colorAssignment.cd;
    const doorLeftPct = (stim.door_position_px / DOOR_CANVAS_WIDTH) * 100;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-end", height: "100%", width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            width: { xs: "70%", sm: "60%", md: 480 },
            maxWidth: 480,
            height: `${Math.round(stim.rect_height_px * busScale)}px`,
            border: "2px solid #ffffff",
            borderTop: "none",
          }}
        >
          {/* Roof */}
          <Box
            sx={{
              position: "absolute",
              top: -12,
              left: -2,
              right: -2,
              height: "12px",
              backgroundColor: busColor,
              borderRadius: "3px 3px 0 0",
            }}
          />
          {/* Door line */}
          <Box
            sx={{
              position: "absolute",
              top: 4,
              bottom: 4,
              left: `${doorLeftPct}%`,
              width: "3px",
              backgroundColor: busColor,
              borderRadius: 1,
            }}
          />
        </Box>
      </Box>
    );
  };

  const phase = cfg.phases[Math.min(phaseIdx, cfg.phases.length - 1)];
  const totalRounds = phase?.rounds ?? 0;
  // Progresso em dois níveis, ambos saindo da config da fase em vigor:
  // blocos (`rounds`) e figuras dentro do bloco (`trials_per_round`, que é
  // exatamente o tamanho da sequência gerada por buildTrialSequence).
  const trialsPerRound = phase?.trials_per_round ?? 0;
  const displayedTrialNum =
    trialsPerRound > 0 ? Math.min(Math.max(currentTrialNum, 1), trialsPerRound) : 0;
  const currentRoundFill =
    trialsPerRound > 0 ? Math.min(1, currentTrialNum / trialsPerRound) : 0;
  // Só a fase mista traz categorias dos dois grupos (A/B e C/D) ao mesmo tempo,
  // então é a única em que a cor da borda separaria os botões em dois blocos.
  // Nela a borda fica neutra, sem cor de categoria nem no hover.
  const useNeutralButtonBorder = phase?.structure === "mixed";

  const safeIntroPageIndex = Math.min(introPageIndex, Math.max(0, introductionPages.length - 1));
  const currentIntroPage = introductionPages[safeIntroPageIndex];
  const isLastIntroPage = safeIntroPageIndex >= introductionPages.length - 1;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        boxSizing: "border-box",
        margin: "0 auto",
        mt: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* INTRO */}
      {screen === "intro" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
            {richText(currentIntroPage.title)}
          </Typography>
          <Box
            component="ul"
            sx={{
              textAlign: "left",
              listStyleType: "disc",
              paddingLeft: "1.25rem",
              margin: 0,
              marginBottom: "1rem",
            }}
          >
            {currentIntroPage.instructions.map((line: string, i: number) => (
              <Box component="li" key={i} sx={{ marginBottom: "0.4rem" }}>
                <Typography variant="body1" component="div" color="text.secondary">
                  {richText(line)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 999, px: 4, color: "white" }}
              onClick={advanceIntro}
            >
              {isLastIntroPage ? "Iniciar" : "Continuar"}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Pressione o botão ou a barra de espaço para dar seguimento.
            </Typography>
          </Box>
        </Box>
      )}

      {/* PHASE INTRO */}
      {screen === "phase_intro" && (() => {
        const currentPhase = cfg.phases[phaseIdx];
        const defaults = getPhaseIntroDefaults(keyAssignment, currentPhase)[currentPhase?.structure] ?? { title: `Fase ${currentPhase?.phase}`, text: "" };
        const intro = currentPhase?.introduction;
        const phaseTitle = intro?.title ?? defaults.title;
        const phaseText = intro?.text ?? defaults.text;
        const phaseInstructions = intro?.instructions ?? defaults.instructions;
        return (
          <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
            <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
              {richText(phaseTitle)}
            </Typography>
            {/* Alinhado à esquerda: as telas de fase são parágrafos + lista,
                como nas imagens de especificação (o container é centralizado). */}
            <Typography
              variant="body1"
              component="div"
              color="text.secondary"
              sx={{ mb: 2, textAlign: "left" }}
            >
              {richText(phaseText)}
            </Typography>
            {phaseInstructions && phaseInstructions.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  textAlign: "left",
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Instruções
                </Typography>
                <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                  {phaseInstructions.map((line: string, i: number) => (
                    <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                      <Typography variant="body1" component="div" color="text.secondary">{richText(line)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {renderLegend(getPhaseLegend(phaseIdx))}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 3 }}>
              {isStartingCountdown ? (
                <Typography variant="h5" color="primary">
                  {`Começando em ${countdownValue}...`}
                </Typography>
              ) : (
                <>
                  <Button
                    variant="contained"
                    sx={{ borderRadius: 999, px: 4, color: "white" }}
                    onClick={beginPhaseRound}
                  >
                    Começar
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Pressione o botão ou a barra de espaço para dar seguimento na tarefa.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        );
      })()}

      {/* TRIAL / FEEDBACK / ISI */}
      {(screen === "trial" || screen === "feedback" || screen === "isi") && (
        <Box
          ref={darkBoxRef}
          sx={{
            width: "100%",
            backgroundColor: "#08080F",
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            color: "#E8E8F0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            height: darkBoxTop > 0 ? { xs: `calc(100dvh - ${darkBoxTop}px - 2rem)`, md: `calc(100dvh - ${darkBoxTop}px - 3rem)` } : undefined,
            boxSizing: "border-box",
          }}
        >
          {/* Header + progresso, em uma única linha: o indicador de blocos fica
              logo ao lado de "Bloco x", compacto, para que a única barra de
              largura total na caixa continue sendo a de tempo. O preenchimento
              usa o neutro da caixa (#E8E8F0) em vez de ciano/magenta — essas
              duas cores codificam categoria na figura e nos botões. */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              columnGap: 1.5,
              rowGap: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ color: "#00E5FF" }}>
                Fase {cfg.phases[phaseIdx]?.phase} · Bloco {roundIdx + 1}/{totalRounds}
              </Typography>
              {totalRounds > 0 && (
                // Um segmento por bloco da fase: blocos concluídos ficam cheios,
                // o atual acompanha as figuras e os seguintes ficam vazios.
                <Box
                  sx={{
                    display: "flex",
                    gap: "2px",
                    flexShrink: 0,
                    width: { xs: 80, sm: 120 },
                  }}
                >
                  {Array.from({ length: totalRounds }, (_unused, i: number) => {
                    const fill = i < roundIdx ? 1 : i === roundIdx ? currentRoundFill : 0;
                    return (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          height: 5,
                          backgroundColor: "#1A1A25",
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${fill * 100}%`,
                            backgroundColor: "rgba(232,232,240,0.75)",
                            transition: "width 0.2s linear",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
            {trialsPerRound > 0 && (
              <Typography variant="body2" noWrap sx={{ color: "#aaa" }}>
                Figura {displayedTrialNum} de {trialsPerRound}
              </Typography>
            )}
          </Box>

          {/* Time bar */}
          <Box sx={{ width: "100%", height: 4, backgroundColor: "#1A1A25", borderRadius: 2, overflow: "hidden" }}>
            {screen === "trial" && (
              <Box
                sx={{
                  height: "100%",
                  width: `${timeLeftPct * 100}%`,
                  backgroundColor: timeLeftPct > 0.5 ? "#00FF9D" : timeLeftPct > 0.25 ? "#FFD700" : "#FF4444",
                  transition: "width 0.05s linear",
                }}
              />
            )}
          </Box>

          {/* Stimulus area */}
          <Box
            ref={stimAreaRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {screen === "trial" && stimulus && renderBus(stimulus)}
            {screen === "feedback" && (
              feedbackType === "correct" ? (
                <CheckCircleIcon sx={{ fontSize: 64, color: "#00FF9D" }} />
              ) : feedbackType === "omission" ? (
                <HourglassEmptyIcon sx={{ fontSize: 64, color: "#FFD700" }} />
              ) : (
                <CancelIcon sx={{ fontSize: 64, color: "#FF4444" }} />
              )
            )}
            {screen === "isi" && (
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#333" }} />
            )}
          </Box>

          {/* Touch response buttons — sempre visíveis em dispositivos de
              toque (sem teclado físico); no desktop só durante a dica
              inicial (ver isCoarsePointer em deviceInput.ts). */}
          {screen === "trial" && (isCoarsePointer || tipVisible) && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              {getPhaseLegend(phaseIdx).map(({ key, color, desc }) => {
                const borderColor = useNeutralButtonBorder ? NEUTRAL_BUTTON_BORDER : color;
                return (
                  <Button
                    key={key}
                    onClick={() => handleResponse(key)}
                    sx={{
                      p: "10px 14px",
                      minWidth: 80,
                      minHeight: 56,
                      background: "#111118",
                      border: `2px solid ${borderColor}55`,
                      borderRadius: 2,
                      color: "#E8E8F0",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      "&:hover": { background: "#1E1E2E", border: `2px solid ${borderColor}` },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#E8E8F0", textAlign: "center", maxWidth: 110, lineHeight: 1.2, textTransform: "none" }}>
                      {tipVisible ? desc : key}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ROUND BREAK */}
      {screen === "round_break" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            Rodada {roundIdx + 1} concluída
          </Typography>
          <Box sx={{ display: "flex", gap: 3, justifyContent: "center", mb: 3 }}>
            {(
              [
                ["Acertos", roundStats.correct, "#43a047"],
                ["Erros", roundStats.error, "#e53935"],
                ["Omissões", roundStats.omission, "#fb8c00"],
              ] as const
            ).map(([label, value, color]) => (
              <Box key={label}>
                <Typography variant="h3" sx={{ color, fontWeight: 700 }}>
                  {value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            sx={{ borderRadius: 999, px: 4, color: "white" }}
            onClick={onContinueRound}
          >
            Continuar
          </Button>
        </Box>
      )}

      {/* DONE */}
      {screen === "done" && (
        <Box sx={{ width: "100%", backgroundColor: "grey.100", borderRadius: 2, p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            Tarefa concluída!
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            {phaseSummaries.map((s) => (
              <Box
                key={s.phase}
                sx={{
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: { xs: 1.5, md: 2 },
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                  Fase {s.phase}
                </Typography>
                <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
                  {(
                    [
                      ["Acertos", s.correct, "#43a047"],
                      ["Erros", s.error, "#e53935"],
                      ["Omissões", s.omission, "#fb8c00"],
                    ] as const
                  ).map(([label, value, color]) => (
                    <Box key={label}>
                      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
                        {value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography variant="body1" color="text.secondary">
            Clique em "Próximo" para continuar.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CategoriesQuestion;
